import React, { useEffect, useState, useMemo } from 'react';
import MultiSelectCheckboxGroup from '../../components/CheckBoxFilter';
import VerticalTable from '../../components/verticalTable';
import { useTranslation } from 'react-i18next';
import { previousMonthEndFormatted, previousMonthStartFormatted } from '../../constants/intex';
import { generateWasIs } from '../../services/wasIs';
import { listBranches } from '../../services/filterApis';
import VerticalTable2 from '../../components/verticalTable2';

// Updated Branch colors for comparative mode
const BRANCH_COLORS = [
  { 
    bgColor: '#E3F2FD',  // Light Blue background
    textColor: '#0D47A1', // Dark Blue text
    borderColor: '#90CAF9' // Blue border
  },
  { 
    bgColor: '#E8F5E9',  // Light Green background
    textColor: '#1B5E20', // Dark Green text
    borderColor: '#A5D6A7' // Green border
  },
  { 
    bgColor: '#FFF3E0',  // Light Orange background
    textColor: '#E65100', // Dark Orange text
    borderColor: '#FFCC80' // Orange border
  }
];

type TableRow = {
  category: string;
  values: string[];
};

type BranchColor = {
  bgColor: string;
  textColor: string;
  borderColor: string;
};

const WasIs = () => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [comparativeLoading, setComparativeLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isValid, setIsValid] = useState(true);
  const [isDownloadDisabled, setIsDownloadDisabled] = useState(false);
  
  // Consolidated data states
  const [countData, setCountData] = useState<TableRow[]>([]);
  const [percentData, setPercentData] = useState<TableRow[]>([]);
  const [countHeaders, setCountHeaders] = useState<string[]>([]);
  const [columnTotals, setColumnTotals] = useState<string[]>([]);
  
  // Comparative data states
  const [comparativeCountData, setComparativeCountData] = useState<TableRow[]>([]);
  const [comparativePercentData, setComparativePercentData] = useState<TableRow[]>([]);
  const [comparativeHeaders, setComparativeHeaders] = useState<string[]>([]);
  const [comparativeColumnTotals, setComparativeColumnTotals] = useState<string[]>([]);
  const [branchColors, setBranchColors] = useState<Record<string, BranchColor>>({});

  const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>({
    periodWithOutDay: ["month"],
    fromDate: [previousMonthStartFormatted],
    toDate: [previousMonthEndFormatted],
    displaymode: ["1"]
  });

  const [pendingFilters, setPendingFilters] = useState<Record<string, string[]>>({
    periodWithOutDay: ["month"],
    fromDate: [previousMonthStartFormatted],
    toDate: [previousMonthEndFormatted],
    displaymode: ["1"]
  });

  const [branchList, setBranchList] = useState<{ branch_id: string; branch_name: string }[]>([]);
  const [applyFilter, setApplyFilter] = useState(0);

  const filters = [
    { key: "state", type: "select", label: t("filters.state") },
    { key: "cluster", type: "select", label: t("filters.cluster") },
    {
      key: "displaymode",
      type: "singleSelect",
      label: t("filters.displaymode"),
    },
    { key: "branch", type: "select", label: t("filters.branch") },
    { key: "fromDate", type: "date", label: t("filters.fromDate"), maxValue: pendingFilters.toDate },
    { key: "toDate", type: "date", label: t("filters.toDate"), maxValue: previousMonthEndFormatted },
  ];

  const branchIdToNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    branchList.forEach((branch) => {
      const id = branch?.branch_id;
      const name = branch?.branch_name;
      if (id && name) {
        map[id] = name;
      }
    });
    return map;
  }, [branchList]);

  const getBranchName = (branchId: string): string => {
    return branchIdToNameMap[branchId] || branchId;
  };

  const handleFilterChange = (updatedValues: Record<string, string[]>) => {
    setPendingFilters(updatedValues);
  };

  const handleCancelClick = () => {
    setPendingFilters({
      periodWithOutDay: ["month"],
      fromDate: [previousMonthStartFormatted],
      toDate: [previousMonthEndFormatted],
      displaymode: ["1"]
    });
    setApplyFilter(0);
    setPendingFilters({
      periodWithOutDay: ["month"],
      fromDate: [previousMonthStartFormatted],
      toDate: [previousMonthEndFormatted],
      displaymode: ["1"],
    });
    setValidationError(null);
    setIsValid(true);
    setIsDownloadDisabled(false);
  };

  const handleApplyClick = () => {
    const displaymodeRaw = pendingFilters.displaymode?.[0] || "consolidate";
    const displaymode = displaymodeRaw.trim().toLowerCase();
    const selectedBranches = pendingFilters.branch || [];
    const isComparative = displaymode === "comparative" || displaymode === "2";

    if (isComparative) {
      if (selectedBranches.length < 2 || selectedBranches.length > 3) {
        setValidationError(
          "Please select minimum two and maximum three branches for comparative mode."
        );
        setIsValid(false);
        setIsDownloadDisabled(true);
        return;
      }
    }

    setSelectedFilters(pendingFilters);
    setValidationError(null);
    setIsValid(true);
    setIsDownloadDisabled(false);
    setApplyFilter(prev => prev + 1);
  };

  const handleDownloadClick = () => {
    // Implement download functionality here
    alert("Download functionality to be implemented");
  };

  const transformData = (apiData: any) => {
    const wasData = apiData?.dpd_buckets?.was ?? [];

    const countData: TableRow[] = wasData.map((wasEntry: any) => {
      const values = wasEntry.is.map((isEntry: any) => isEntry.count.toString());
      return {
        category: wasEntry.bucket_name,
        values,
      };
    });

    const percentData: TableRow[] = wasData.map((wasEntry: any) => {
      const values = wasEntry.is.map((isEntry: any) =>
        `${isEntry.percentage?.toFixed(1) ?? "0"}%`
      );
      return {
        category: wasEntry.bucket_name,
        values,
      };
    });

    const headers =
      wasData.length > 0
        ? ["Was", ...wasData[0].is.map((isEntry: any) => isEntry.bucket_name), "Total"]
        : [];

    // Add row totals
    countData.forEach((row, idx) => {
      const total = row.values.reduce((sum, val) => sum + parseInt(val || "0"), 0);
      countData[idx].values.push(total.toString());
    });

    percentData.forEach(row => {
      row.values.push("100%");
    });

    // Calculate column totals (exclude last 'Total' column)
    const colCount = countData?.[0]?.values?.length ?? 0;
    const columnTotals: string[] = [];
    for (let colIdx = 0; colIdx < colCount - 1; colIdx++) {
      let colSum = 0;
      countData.forEach(row => {
        const val = parseInt(row.values[colIdx]) || 0;
        colSum += val;
      });
      columnTotals.push(colSum.toString());
    }

    return { countData, percentData, headers, columnTotals };
  };

  const transformComparativeData = (apiDataArray: any[], branchIds: string[]) => {
    if (!apiDataArray || apiDataArray.length === 0) {
      return {
        countData: [],
        percentData: [],
        headers: [],
        columnTotals: []
      };
    }

    // Get all unique bucket names from all branches
    const allBucketNames = Array.from(new Set(
      apiDataArray.flatMap(data => 
        data?.dpd_buckets?.was?.map((was: any) => was.bucket_name) || []
    )));

    // Get DPD bucket names from first branch
    const dpdBuckets = apiDataArray[0]?.dpd_buckets?.was?.[0]?.is || [];
    const dpdBucketNames = dpdBuckets.map((bucket: any) => bucket.bucket_name);

    // Create headers structure
    const headers = [
      "Was", 
      ...dpdBucketNames.flatMap((bucketName: string)  => 
        branchIds.map(branchId => `${bucketName}\n(${getBranchName(branchId)})`)
      ),
      "Total"
    ];

    // Initialize count and percent data
    const countData: TableRow[] = [];
    const percentData: TableRow[] = [];

    allBucketNames.forEach(bucketName => {
      const countValues: string[] = [];
      const percentValues: string[] = [];

      // For each DPD bucket
      dpdBuckets.forEach((_: any, colIdx: number) => {
        // For each branch
        apiDataArray.forEach(data => {
          const wasEntry = data?.dpd_buckets?.was?.find((was: any) => was.bucket_name === bucketName);
          if (wasEntry && wasEntry.is[colIdx]) {
            countValues.push(wasEntry.is[colIdx].count.toString());
            percentValues.push(`${wasEntry.is[colIdx].percentage?.toFixed(1) ?? "0"}%`);
          } else {
            countValues.push("0");
            percentValues.push("0%");
          }
        });
      });

      countData.push({
        category: bucketName,
        values: countValues
      });

      percentData.push({
        category: bucketName,
        values: percentValues
      });
    });

    // Add row totals
    countData.forEach((row, idx) => {
      const total = row.values.reduce((sum, val) => sum + parseInt(val || "0"), 0);
      countData[idx].values.push(total.toString());
    });

    percentData.forEach(row => {
      row.values.push("100%");
    });

    // Calculate column totals (exclude last 'Total' column)
    const columnTotals: string[] = [];
    for (let colIdx = 0; colIdx < dpdBuckets.length * branchIds.length; colIdx++) {
      let colSum = 0;
      countData.forEach(row => {
        colSum += parseInt(row.values[colIdx] || "0");
      });
      columnTotals.push(colSum.toString());
    }

    // Assign colors to branches
    const colors: Record<string, BranchColor> = {};
    branchIds.forEach((branchId, index) => {
      colors[branchId] = BRANCH_COLORS[index % BRANCH_COLORS.length];
    });
    setBranchColors(colors);

    return { countData, percentData, headers, columnTotals };
  };

  const generateWasIsData = async () => {
    const displaymodeRaw = selectedFilters.displaymode?.[0] || "1";
    const displaymode = displaymodeRaw.trim().toLowerCase();
    const isComparative = displaymode === "comparative" || displaymode === "2";

    if (isComparative) {
      setComparativeLoading(true);
      try {
        const selectedBranches = selectedFilters.branch || [];

        const branchPromises = selectedBranches.map(branchId => 
          generateWasIs({
            ...selectedFilters,
            branch: [branchId]
          })
        );

        const branchResponses = await Promise.all(branchPromises);
        
        const transformed = transformComparativeData(
          branchResponses.map(r => r.data),
          selectedBranches
        );

        setComparativeCountData(transformed.countData);
        setComparativePercentData(transformed.percentData);
        setComparativeHeaders(transformed.headers);
        setComparativeColumnTotals(transformed.columnTotals);

      } catch (err) {
        setError("Failed to fetch comparative Was-Is data");
        console.error(err);
      } finally {
        setComparativeLoading(false);
      }
    } else {
      setLoading(true);
      try {
        const response = await generateWasIs(selectedFilters);
        const transformed = transformData(response.data);
        
        setCountData(transformed.countData);
        setPercentData(transformed.percentData);
        setCountHeaders(transformed.headers);
        setColumnTotals(transformed.columnTotals);
        
      } catch (err) {
        setError("Failed to fetch Was-Is data");
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    generateWasIsData();
  }, [applyFilter]);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const response = await listBranches(selectedFilters.state || []);
        const branches = Array.isArray(response?.data) 
          ? response.data 
          : Array.isArray(response) 
            ? response 
            : [];
        setBranchList(branches);
      } catch (error) {
        console.error("Error loading branches", error);
        setBranchList([]);
      }
    };

    fetchBranches();
  }, [selectedFilters.state]);

  const isComparativeMode = selectedFilters.displaymode?.[0]?.trim().toLowerCase() === "2";

  return (
    <div>
      <div className="page-content">
        <div className="header">
          {t('wasIs.pageTitle')}
        </div>

        <div className="content px-3">
          <MultiSelectCheckboxGroup 
            filters={filters}
            selectedValues={pendingFilters}
            onChange={handleFilterChange}
            isLoading={[loading, comparativeLoading]}
            buttons={[
              { label: t("buttons.apply"), onClick: handleApplyClick },
              { label: t("buttons.cancel"), onClick: handleCancelClick },
              { 
                label: t("buttons.download"), 
                onClick: handleDownloadClick,
                disabled: isDownloadDisabled
              },
            ]}
          />
          
          {validationError && (
            <div
              style={{
                color: "#B00020",
                backgroundColor: "#FDECEA",
                padding: "12px 16px",
                border: "1px solid #F5C6CB",
                borderRadius: "6px",
                fontWeight: "500",
                marginTop: "10px",
                boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
              }}
            >
              ⚠️ {validationError}
            </div>
          )}
        </div>

        {!validationError && applyFilter && isComparativeMode ? (
          <>
            <div className="p-2 me-3">
              <h2
                style={{
                  fontSize: 15,
                  fontWeight: "bold",
                  fontFamily: 'Arial',
                  textAlign: "start",
                  paddingLeft: "15px",
                  color: '#14213d',
                }}
              >
                {t('wasIs.wasIsCount')}
              </h2>
              {comparativeCountData.length > 0 && comparativeColumnTotals.length > 0 && (
                <VerticalTable2
                  headers={comparativeHeaders}
                  data={[
                    ...comparativeCountData,
                    {
                      category: t('Column Totals'),
                      values: [...comparativeColumnTotals, '']
                    },
                  ]}
                  loading={comparativeLoading}
                  isComparative={true}
                  branchCount={selectedFilters.branch?.length || 0}
                  branchColors={branchColors}
                  branchIds={selectedFilters.branch || []}
                />
              )}
            </div>

            <div className="p-2 me-3 mt-3">
              <h2
                style={{
                  fontSize: 15,
                  fontWeight: "bold",
                  fontFamily: 'Arial',
                  textAlign: "start",
                  paddingLeft: "15px",
                  color: '#14213d',
                }}
              >
                {t('wasIs.wasIsPercent')}
              </h2>
              <VerticalTable2 
                headers={comparativeHeaders} 
                data={comparativePercentData} 
                loading={comparativeLoading} 
                isComparative={true}
                branchCount={selectedFilters.branch?.length || 0}
                branchColors={branchColors}
                branchIds={selectedFilters.branch || []}
              />
            </div>
          </>
        ) : (
          !validationError && (
          <>
            <div className="p-2 me-3">
              <h2
                style={{
                  fontSize: 15,
                  fontWeight: "bold",
                  fontFamily: 'Arial',
                  textAlign: "start",
                  paddingLeft: "15px",
                  color: '#14213d',
                }}
              >
                {t('wasIs.wasIsCount')}
              </h2>
              {countData.length > 0 && columnTotals.length > 0 && (
                <VerticalTable
                  headers={countHeaders}
                  data={[
                    ...countData,
                    {
                      category: t('Column Totals'),
                      values: [...columnTotals, '']
                    },
                  ]}
                  loading={loading}
                />
              )}
            </div>

            <div className="p-2 me-3 mt-3">
              <h2
                style={{
                  fontSize: 15,
                  fontWeight: "bold",
                  fontFamily: 'Arial',
                  textAlign: "start",
                  paddingLeft: "15px",
                  color: '#14213d',
                }}
              >
                {t('wasIs.wasIsPercent')}
              </h2>
              <VerticalTable 
                headers={countHeaders} 
                data={percentData} 
                loading={loading} 
              />
            </div>
              
          </>
        ))}
      </div>
    </div>
  );
};

export default WasIs;